// File: src/components/PhotoGallery.js
import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { client } from "../lib/sanity";

export default function PhotoGallery() {
  const [gallery, setGallery] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    client
      .fetch(
        `*[_type == "photoGallery" && title == "Photos of Me"][0]{
          title,
          photos[] {
            asset->{url},
            alt,
            caption
          }
        }`
      )
      .then(setGallery);
  }, []);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  // Escape key closes lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e) => {
      if (e.key === "Escape") closeLightbox();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [lightboxIndex, closeLightbox]);

  if (!gallery) return null;

  return (
    <Wrapper>
      <Grid>
        {gallery.photos.map((photo, index) => (
          <GalleryItem
            key={index}
            role="button"
            tabIndex={0}
            aria-label={`View photo: ${photo.alt || `Photo ${index + 1}`}`}
            onClick={() => setLightboxIndex(index)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setLightboxIndex(index);
              }
            }}
            $span={index % 10 === 0 || index % 7 === 3 ? 2 : 1}
          >
            <img
              src={photo.asset.url}
              alt={photo.alt || ""}
              loading="lazy"
            />
          </GalleryItem>
        ))}
      </Grid>

      {lightboxIndex !== null && (
        <LightboxOverlay
          role="dialog"
          aria-modal="true"
          aria-label="Photo lightbox"
          onClick={closeLightbox}
        >
          <LightboxContent onClick={(e) => e.stopPropagation()}>
            <img
              src={gallery.photos[lightboxIndex].asset.url}
              alt={gallery.photos[lightboxIndex].alt || ""}
            />
            {gallery.photos[lightboxIndex].caption && (
              <Caption>{gallery.photos[lightboxIndex].caption}</Caption>
            )}
            <CloseButton
              onClick={closeLightbox}
              aria-label="Close lightbox"
            >
              ×
            </CloseButton>
          </LightboxContent>
        </LightboxOverlay>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-top: 2rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  grid-auto-rows: 150px;
  grid-auto-flow: dense;
  gap: 0.5rem;
  max-height: 700px;
  overflow: hidden;

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    grid-auto-rows: 110px;
    max-height: 440px;
  }
`;

const GalleryItem = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 0.5rem;
  cursor: pointer;
  grid-column: span ${(props) => props.$span};
  grid-row: span ${(props) => props.$span};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img,
  &:focus img {
    transform: scale(1.05);
  }

  &:focus-visible {
    outline: 2px solid #C4A25D;
    outline-offset: 2px;
  }
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 1rem;
`;

const LightboxContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  text-align: center;

  img {
    max-width: 100%;
    max-height: 80vh;
    border-radius: 0.5rem;
  }
`;

const Caption = styled.p`
  color: white;
  margin-top: 1rem;
  font-size: 1rem;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -20px;
  right: -20px;
  background: white;
  color: #1f2937;
  border: none;
  font-size: 1.25rem;
  line-height: 1;
  border-radius: 999px;
  width: 44px;
  height: 44px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: #f3f4f6;
  }

  &:focus-visible {
    outline: 2px solid #C4A25D;
    outline-offset: 2px;
  }
`;
